import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCommercialList } from "@/api/hooks";
import { NewLeadDialog } from "@/components/sales/NewLeadDialog";
import { BusinessStage, stageForCommercial } from "@/components/shared/BusinessStage";
import { Search, History, Plus } from "lucide-react";

// Phase 4.6 — real Lead + Inquiry pipeline (GET /commercial/inquiries),
// replacing the earlier board that rendered pre-Phase-3 seed/mock records.
// Deliberately simple columns, not a full drag-and-drop board — Sales opens
// a card and lands directly in the unified commercial workspace.
function Column({ title, hint, count, children, empty }) {
  return (
    <div className="flex flex-col w-[300px] shrink-0">
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="flex items-center gap-2">
          <span className="font-display font-semibold text-sm text-slate-700">{title}</span>
          <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5">{count}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{hint}</span>
      </div>
      <div className="flex-1 space-y-2">{count === 0 ? <div className="text-[12px] text-slate-400 px-1 py-3">{empty}</div> : children}</div>
    </div>
  );
}

function Card({ onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-indigo-300 hover:shadow-sm transition-all">{children}</button>;
}

export default function SalesPipeline() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  // Phase 4.7: closed (Lost) opportunities are excluded from the default
  // pipeline view server-side (GET /commercial/inquiries) so they don't
  // clutter active work — but remain one click away via "Show closed" or a
  // search, never deleted or hidden from history.
  const [showClosed, setShowClosed] = useState(false);
  const { data, isLoading } = useCommercialList(q ? { q } : (showClosed ? { includeClosed: true } : {}));
  if (isLoading || !data) return <Loading />;

  const leads = data.leads || [];
  const inquiries = data.inquiries || [];
  const requestedView = new URLSearchParams(location.search || "").get("view") || "all";
  const view = ["new-leads", "quotation", "accepted"].includes(requestedView) ? requestedView : "all";
  const showColumn = (column) => view === "all" || view === column;
  const visibleLeads = view === "new-leads" ? leads.filter((lead) => lead.status === "NEW") : leads;
  const isAccepted = (inquiry) => inquiry.status === "WON" || inquiry.acceptanceStatus === "VERIFIED";
  const hasSentQuotation = (inquiry) => Boolean(inquiry.quotationSentAt || Number(inquiry.sentVersionsCount) > 0);
  const active = inquiries.filter((i) => !isAccepted(i) && !["LOST", "EXPIRED", "ON_HOLD_CUSTOMER"].includes(i.status));
  const sent = active.filter(hasSentQuotation);
  const inProgress = active.filter((i) => !hasSentQuotation(i));
  const accepted = inquiries.filter(isAccepted);
  const lost = inquiries.filter((i) => i.status === "LOST");

  return (
    <>
      <PageHeader title="Sales pipeline" subtitle="Lead to accepted quotation" actions={<Button size="sm" onClick={() => setNewLeadOpen(true)} data-testid="pipeline-new-lead"><Plus className="mr-1.5 h-4 w-4" />New lead</Button>} />
      <div className="flex-1 overflow-auto p-6">
        <div className="mb-4 max-w-sm relative flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-2.5" />
            <Input placeholder="Search lead, inquiry, customer, route…" className="pl-8 h-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pipeline-search" />
          </div>
          <Button size="sm" variant={showClosed ? "default" : "outline"} className="h-9 shrink-0" onClick={() => setShowClosed((v) => !v)} data-testid="pipeline-toggle-closed">
            <History className="h-3.5 w-3.5 mr-1.5" />{showClosed ? "Hide closed" : "Show closed"}
          </Button>
        </div>
        <div className="flex gap-4 min-w-max">
          {showColumn("new-leads") && <Column title="Leads" hint="Unqualified" count={visibleLeads.length} empty="No untouched enquiries — you're caught up.">
            {visibleLeads.map((l) => (
              <Card key={l.id} testid={`pipe-lead-${l.id}`} onClick={() => navigate(`/sales/leads/${l.id}`)}>
                <div className="flex items-center justify-between mb-1"><span className="font-semibold text-sm text-slate-800 truncate">{l.company || l.id}</span><StatusPill status={l.status} /></div>
                <div className="text-[12px] text-slate-500">{l.source}{l.contactEmail ? ` · ${l.contactEmail}` : ""}</div>
                <div className="text-[11px] text-slate-400 font-mono-num mt-1">{(l.createdAt || "").slice(0, 10)}</div>
                <div className="mt-2"><BusinessStage stage={stageForCommercial(l, "lead")} /></div>
              </Card>
            ))}
          </Column>}

          {view === "all" && <Column title="Requirement" hint="Not yet quoted" count={inProgress.length} empty="No inquiries waiting on requirements right now.">
            {inProgress.map((i) => (
              <Card key={i.id} testid={`pipe-inq-${i.id}`} onClick={() => navigate(i.clientId ? `/sales/customers/${i.clientId}?inquiry=${i.id}` : `/sales/inquiries/${i.id}`)}>
                <div className="flex items-center justify-between mb-1"><span className="font-semibold text-sm text-slate-800 truncate">{i.customer}</span><StatusPill status={i.status} /></div>
                <div className="text-[12px] text-slate-500 mb-1">{i.mode} · {i.lane}</div>
                {i.missingBlockingCount > 0 && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5">{i.missingBlockingCount} blocking item(s) missing</div>}
                <div className="mt-2"><BusinessStage stage={stageForCommercial(i)} /></div>
                <div className="mt-1.5 text-[11px] font-medium text-indigo-600">Open workspace →</div>
              </Card>
            ))}
          </Column>}

          {showColumn("quotation") && <Column title="Quotation" hint="Sent / negotiating" count={sent.length} empty="No quotations awaiting a customer response.">
            {sent.map((i) => (
              <Card key={i.id} testid={`pipe-qtn-${i.id}`} onClick={() => navigate(i.clientId ? `/sales/customers/${i.clientId}?inquiry=${i.id}` : `/sales/inquiries/${i.id}`)}>
                <div className="flex items-center justify-between mb-1"><span className="font-semibold text-sm text-slate-800 truncate">{i.customer}</span><StatusPill status={i.acceptanceStatus || i.quotationStatus || i.status} /></div>
                <div className="text-[12px] text-slate-500 mb-1.5">{i.lane}</div>
                <div className="text-[11px] text-slate-400">{i.sentVersionsCount > 1 ? `${i.sentVersionsCount} versions sent` : "Sent"} {(i.quotationSentAt || "").slice(0, 10)}</div>
                {/* Item 2 (Aug 2026 Pipeline fix) — Sales approves a quotation
                    by verifying real recorded customer acceptance (never a
                    bare judgment call — see server.py list route). When one
                    is ready, surface exactly which version and its terms
                    directly on the board so Sales doesn't have to open every
                    card to find out; the actual approve action is the
                    existing "Verify acceptance" step one click away inside. */}
                {i.pendingAcceptance && (
                  <div className="mt-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                    Ready to approve — V{i.pendingAcceptance.version} · {i.pendingAcceptance.amount != null ? `${i.pendingAcceptance.amount} ${i.pendingAcceptance.currency || ""}` : "—"} · {i.pendingAcceptance.evidenceType} via {i.pendingAcceptance.source}
                  </div>
                )}
                <div className="mt-2"><BusinessStage stage={stageForCommercial(i)} /></div>
                {i.pendingAcceptance && <div className="mt-1.5 text-[11px] font-medium text-emerald-700">Review &amp; approve →</div>}
              </Card>
            ))}
          </Column>}

          {showColumn("accepted") && <Column title="Accepted" hint="Ready for Finance" count={accepted.length} empty="No accepted orders yet.">
            {accepted.map((i) => (
              <Card key={i.id} testid={`pipe-won-${i.id}`} onClick={() => navigate(i.clientId ? `/sales/customers/${i.clientId}?inquiry=${i.id}` : `/sales/inquiries/${i.id}`)}>
                <div className="flex items-center justify-between mb-1"><span className="font-semibold text-sm text-slate-800 truncate">{i.customer}</span><StatusPill status="VERIFIED" /></div>
                <div className="text-[12px] text-slate-500 mb-1.5">{i.lane}</div>
                <div className="text-[11px] text-emerald-600 font-medium">→ Finance review</div>
                <div className="mt-2"><BusinessStage stage={stageForCommercial(i)} /></div>
              </Card>
            ))}
          </Column>}

          {view === "all" && showClosed && (
            <Column title="Closed / Lost" hint="History" count={lost.length} empty="No closed opportunities.">
              {lost.map((i) => (
                <Card key={i.id} testid={`pipe-lost-${i.id}`} onClick={() => navigate(i.clientId ? `/sales/customers/${i.clientId}?inquiry=${i.id}` : `/sales/inquiries/${i.id}`)}>
                  <div className="flex items-center justify-between mb-1"><span className="font-semibold text-sm text-slate-800 truncate">{i.customer}</span><StatusPill status="LOST" /></div>
                  <div className="text-[12px] text-slate-500 mb-1.5">{i.lane}</div>
                  <div className="text-[11px] text-slate-400">Reason: {i.lostReasonCode || "—"}</div>
                  <div className="mt-2"><BusinessStage stage={stageForCommercial(i)} /></div>
                </Card>
              ))}
            </Column>
          )}
        </div>
        {leads.length + inquiries.length === 0 && <EmptyState title="Nothing in the pipeline yet" sub="New leads and inquiries will appear here as they come in." />}
      </div>
      <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} />
    </>
  );
}
